//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React, { useEffect, useState } from 'react'

import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import { aprToDisplay, erc20WeiToCommaDecString } from '@shared/conversions'

import ButtonModal from '@react/components/ButtonModal'
import ViewLogo from '@react/components/elements/ViewLogo'
import {
  PoolAccountCTO,
  PoolBaseOverviewCTO,
  PoolFlexOverviewCTO,
  PoolOverviewCTO,
  poolIsOpenForInvestment,
} from '@shared/sharedTypes'
import { Link, useNavigate } from 'react-router-dom'

import LenderDepositToPool from './LenderDepositToPool'

import { PoolTypeEnum } from '@shared/Enums'

import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'

import { useOtChainState, useOtChainTimestamp, useOtSelector } from '@react/redux/store'
import { buildLenderPoolSummary } from './lenderUtils'
import { TrendingUp } from '@mui/icons-material'
import { buildPills } from '@react/components/elements/InterestPills'
import PoolListSkeleton from '@react/components/pools/PoolListSkeleton'

function PoolOpeningRow(props: { poolBaseOverviewCTO: PoolBaseOverviewCTO }): React.ReactElement {
  const { poolBaseOverviewCTO } = props

  const {
    userConnection: { accountAddr },
  } = useOtChainState()
  const { accountPoolPairs } = useOtSelector((state) => state.poolState)

  const chainTimestamp = useOtChainTimestamp()
  const [hasAccess, setHasAccess] = useState(false)

  const theme = useTheme()
  const poolAccountCTOs = useOtSelector((state) => state.poolState.poolAccountCTOs)
  useEffect(() => {
    if (accountAddr && poolBaseOverviewCTO?.poolAddr && accountPoolPairs[accountAddr]?.[poolBaseOverviewCTO.poolAddr]) {
      setHasAccess(true)
    } else {
      setHasAccess(false)
    }
  }, [accountPoolPairs])
  const navigate = useNavigate()
  let interestPills: React.ReactElement | undefined = undefined
  let poolOverviewCTO: PoolOverviewCTO | undefined = undefined

  if (poolBaseOverviewCTO.poolType === PoolTypeEnum.Term) {
    poolOverviewCTO = poolBaseOverviewCTO as PoolOverviewCTO
    const poolAccountCTO = accountAddr
      ? (poolAccountCTOs?.[poolOverviewCTO.poolAddr]?.[accountAddr] as PoolAccountCTO)
      : undefined
    const investorSummary = buildLenderPoolSummary(poolOverviewCTO, poolAccountCTO)
    const apyPill =
      investorSummary.finalizedApr && investorSummary.finalizedApr > 0 ? (
        <Chip
          sx={{ backgroundColor: theme.palette.success.subtle, marginRight: 2 }}
          icon={<TrendingUp />}
          label={aprToDisplay(Number(investorSummary.finalizedApr)) + '% APY'}
        />
      ) : undefined
    const indicativePill =
      poolOverviewCTO &&
      poolOverviewCTO.requestedLoanState.loanAddr !== ETH_ADDR_ZERO &&
      Number(poolOverviewCTO?.requestedLoanState?.indicativeApr) !== 0 ? (
        <Chip
          sx={{ backgroundColor: theme.palette.accent1.subtle }}
          icon={<TrendingUp />}
          label={`Indicative ${aprToDisplay(poolOverviewCTO?.requestedLoanState?.indicativeApr)}% APY`}
        />
      ) : undefined
    interestPills = (
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        {apyPill}
        {indicativePill}
      </Box>
    )
  } else {
    interestPills = buildPills(poolBaseOverviewCTO as PoolFlexOverviewCTO, theme)
  }

  return (
    <TableRow
      key={poolBaseOverviewCTO.poolAddr}
      onClick={(_) => {
        navigate(`/pools/${poolBaseOverviewCTO.poolAddr}`)
      }}
      sx={{ textDecoration: 'none' }}
    >
      <TableCell
        key={'logo'}
        onClick={(_) => {
          navigate(`/pools/${poolBaseOverviewCTO.poolAddr}`)
        }}
      >
        <ViewLogo style={{ width: '30px', height: '30px' }} poolAddr={poolBaseOverviewCTO.poolAddr} />
      </TableCell>
      <TableCell
        key={'name'}
        align="left"
        onClick={(_) => {
          navigate(`/pools/${poolBaseOverviewCTO.poolAddr}`)
        }}
      >
        <Typography alignContent={'left'} variant="bodyBold" gutterBottom component="div">
          {poolBaseOverviewCTO.name}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>{interestPills}</Box>
      </TableCell>
      <TableCell
        key={'right'}
        align="right"
        onClick={(_) => {
          navigate(`/pools/${poolBaseOverviewCTO.poolAddr}`)
        }}
      >
        <Typography display={'inline'} variant={'caption'}>
          {erc20WeiToCommaDecString(poolBaseOverviewCTO.totalOutstandingLoanPrincipal)}
        </Typography>

        <Typography display={'inline'} variant={'caption'}>
          {' '}
          {poolBaseOverviewCTO.liquidityTokenSymbol}{' '}
        </Typography>
      </TableCell>
      <TableCell key={'nav'} align="right">
        {hasAccess && poolIsOpenForInvestment(poolBaseOverviewCTO, chainTimestamp) && (
          <ButtonModal variant="tertiary" title="Invest">
            <LenderDepositToPool poolAddr={poolBaseOverviewCTO.poolAddr}></LenderDepositToPool>
          </ButtonModal>
        )}
      </TableCell>
    </TableRow>
  )
}

export default function LenderPoolOpenings(_props: { limit?: number }): React.ReactElement {
  const { poolOverviewCTOs, isInitialized } = useOtSelector((state) => state.poolState)
  const { stageConfiguration, chainConfiguration } = useOtSelector((state) => state.chainState)
  const theme = useTheme()

  //  const { chainTimestamp } =useAppSelector((state: RootState) => state.chainState)

  const poolCTOList = Object.values(poolOverviewCTOs).flat()
  // .filter((poolOverviewCTO: PoolOverviewCTO) => {
  //   const canInvest: boolean =
  //     poolOverviewCTO?.rolloverState !== PoolRolloverState.CreateNextLoan &&
  //     poolOverviewCTO?.rolloverState !== PoolRolloverState.EarlyRolloverFinalize &&
  //     poolOverviewCTO?.rolloverState !== PoolRolloverState.RolloverFinalize &&
  //     poolOverviewCTO?.rolloverState !== PoolRolloverState.LateRolloverFinalize &&
  //     chainTimestamp < poolOverviewCTO.requestedLoanState.depositClosingTimestamp

  //   logOT.debug('poolCTOList', canInvest, poolOverviewCTO.totalAssets)

  //   return canInvest && Number(poolOverviewCTO?.totalAssets ?? 0) < 100
  // })

  if (!isInitialized || !stageConfiguration || !chainConfiguration) {
    return <PoolListSkeleton />
  }
  if (poolCTOList.length === 0) {
    return (
      <>
        <Typography align="left" sx={{ fontWeight: 'bold' }} variant={'h4'}>
          Available Loan Vaults
        </Typography>
        <Typography variant="h4" gutterBottom component="div">
          No Openings
        </Typography>
      </>
    )
  }
  return (
    <Paper
      sx={{
        marginTop: 4,
        marginBottom: 4,
        padding: '24px',
        borderRadius: 4,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Typography align="left" sx={{ display: 'inline', float: 'left', fontWeight: 'bold' }} variant={'h4'}>
        Available Loan Vaults
      </Typography>
      <Button
        variant="text"
        component={Link}
        to={'/availableVaults'}
        sx={{ display: 'inline', float: 'right', color: theme.palette.primary.main }}
      >
        <Typography sx={{ color: theme.palette.accent1.emphasis }} variant={'bodyBold'}>
          View All
        </Typography>
      </Button>
      <TableContainer sx={{ maxHeight: 800 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow style={{ backgroundColor: '#000', opacity: 0, zIndex: 0 }}>
              <TableCell style={{ width: '5%', backgroundColor: '#000', opacity: 0, zIndex: 0 }}></TableCell>
              <TableCell
                style={{ width: '45%', backgroundColor: '#000', opacity: 0, zIndex: 0 }}
                align="right"
              ></TableCell>
              <TableCell
                style={{ width: '43%', backgroundColor: '#000', opacity: 0, zIndex: 0 }}
                align="left"
              ></TableCell>
              <TableCell
                style={{ width: '2%', backgroundColor: '#000', opacity: 0, zIndex: 0 }}
                align="left"
              ></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {poolCTOList.map((poolOverviewCTO: PoolBaseOverviewCTO) => {
              return <PoolOpeningRow key={poolOverviewCTO.poolAddr} poolBaseOverviewCTO={poolOverviewCTO} />
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}
